import { ReactNode } from 'react';
import { Skeleton, withStyles } from 'vopay';
import styles from './detailValue.module.scss';

const cn = withStyles(styles);

/**
 * A field in the three states a details tab can be in: `undefined` while its source loads, `null`
 * when that request failed, and the field itself once it arrived, where an empty string is a value
 * the entity simply has no entry for.
 */
export type DetailValue = string | null | undefined;

/**
 * Loading, failed, missing, or the value itself; anything that is not a `DetailValue` renders as
 * given. A failed value is always a plain dash — the toast has already said what failed, and two
 * different dashes down one card read as a typo rather than as two states. A missing value is the
 * same dash unless the caller names a placeholder ("Assigned at activation", "Not provided yet"),
 * which renders in the muted colour the design gives text standing in for a value.
 */
export function renderDetailValue(value: DetailValue | ReactNode, placeholder?: string): ReactNode {
  if (value === undefined) return <Skeleton width={120} height={18} data-testid="detailValueSkeleton" />;
  if (value === null) return '-';
  if (value === '') return placeholder ? <span className={cn('placeholder')}>{placeholder}</span> : '-';

  return value;
}

/** Narrows a source in the same three states down to one of its fields. */
export function pickDetail<Source>(
  source: Source | null | undefined,
  pick: (current: Source) => string | null | undefined,
): DetailValue {
  if (source === undefined) return undefined;
  if (source === null) return null;

  return pick(source) ?? '';
}
